import {useCallback, useLayoutEffect, useRef, useState} from 'react';

export interface SlidingPillStyle {
  left: number;
  top: number;
  width: number;
  height: number;
}

export function useSlidingPill<T extends string>(
  keys: readonly T[],
  activeKey: T | null | undefined,
  options?: {coordinateScale?: number},
) {
  const coordinateScale = options?.coordinateScale ?? 1;
  const trackRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Partial<Record<T, HTMLDivElement | null>>>({});
  const [hoveredKey, setHoveredKey] = useState<T | null>(null);
  const [pillStyle, setPillStyle] = useState<SlidingPillStyle | null>(null);
  const [pillAnimate, setPillAnimate] = useState(false);
  const prevHoveredRef = useRef<T | null | undefined>(undefined);
  const prevActiveRef = useRef(activeKey);

  const highlightedKey = hoveredKey ?? activeKey ?? null;

  const updatePill = useCallback(() => {
    const key = hoveredKey ?? activeKey;
    const track = trackRef.current;
    const item = key ? itemRefs.current[key] : null;

    if (!key || !track || !item) {
      setPillStyle(null);
      return;
    }

    const trackRect = track.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();

    setPillStyle({
      left: (itemRect.left - trackRect.left) / coordinateScale,
      top: (itemRect.top - trackRect.top) / coordinateScale,
      width: itemRect.width / coordinateScale,
      height: itemRect.height / coordinateScale,
    });
  }, [activeKey, coordinateScale, hoveredKey]);

  useLayoutEffect(() => {
    const prevHovered = prevHoveredRef.current;
    const currHovered = hoveredKey;
    let animate = false;

    if (prevHovered === undefined) {
      animate = false;
    } else if (prevHovered === null && currHovered !== null) {
      animate = false;
    } else if (
      prevHovered !== null &&
      currHovered !== null &&
      prevHovered !== currHovered
    ) {
      animate = true;
    } else if (prevHovered !== null && currHovered === null) {
      animate = true;
    }

    if (currHovered === null && prevActiveRef.current !== activeKey) {
      animate = false;
    }

    prevHoveredRef.current = currHovered;
    prevActiveRef.current = activeKey;
    setPillAnimate(animate);
  }, [hoveredKey, activeKey]);

  useLayoutEffect(() => {
    updatePill();
  }, [updatePill]);

  useLayoutEffect(() => {
    if (pillAnimate) return;

    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setPillAnimate(true));
    });

    return () => cancelAnimationFrame(id);
  }, [pillAnimate, pillStyle, hoveredKey, activeKey]);

  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const observer = new ResizeObserver(() => updatePill());
    observer.observe(track);
    keys.forEach(key => {
      const item = itemRefs.current[key];
      if (item) observer.observe(item);
    });

    window.addEventListener('resize', updatePill);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updatePill);
    };
  }, [keys, updatePill]);

  const setItemRef = useCallback(
    (key: T) => (node: HTMLDivElement | null) => {
      itemRefs.current[key] = node;
      requestAnimationFrame(() => updatePill());
    },
    [updatePill],
  );

  const isHighlighted = useCallback(
    (key: T) => highlightedKey === key,
    [highlightedKey],
  );

  const pillTransitionClass = pillAnimate
    ? 'transition-[left,top,width,height] duration-[250ms] ease-out'
    : 'transition-none';

  return {
    trackRef,
    setItemRef,
    setHoveredKey,
    pillStyle,
    pillTransitionClass,
    isHighlighted,
    highlightedKey,
  };
}
